import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { fetchTraceDetail } from '../../../services/traceDetailApi';
import { formatCount, formatCost, formatDate, formatDuration, formatTokens } from '../../../utils/formatters';
import { palette } from '../../../utils/designTokens';
import { shortId } from './rcaHelpers';

/**
 * Accordion row for RcaDetail's "Related Traces" section (inlineTracePreview
 * mode). Expands in place instead of navigating away — lazily fetches a single
 * lightweight trace summary (fetchTraceDetail, not the full spans graph) only
 * on first expand, so opening the drawer never fires N fetches up front.
 */
export default function RelatedTraceRow({ instance, appId, environment, selectedRange, jwt }) {
  const [expanded, setExpanded] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetched, setFetched] = useState(false);

  const traceEnv = instance.environment || environment;
  const traceParams = new URLSearchParams();
  if (traceEnv) traceParams.set('env', traceEnv);
  if (selectedRange) traceParams.set('range', selectedRange);
  const traceUrl = `/app/${appId}/traces/${encodeURIComponent(instance.trace_id)}?${traceParams.toString()}`;

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (!next || fetched || !jwt) return;
    setLoading(true);
    setError(null);
    try {
      const detail = await fetchTraceDetail(instance.trace_id, appId, traceEnv, jwt, {});
      setPreview(detail);
    } catch (err) {
      setError(err?.message || 'Failed to load trace preview');
    } finally {
      setLoading(false);
      setFetched(true);
    }
  };

  return (
    <div className="rounded border border-border-subtle bg-background-primary">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-background-tertiary"
        onClick={toggle}
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-accent-primary-base">{shortId(instance.trace_id)}</span>
            <span className="text-[11px] text-text-muted">{formatDate(instance.updated_at)}</span>
          </div>
          <div className="mt-1 text-xs text-text-muted">
            {instance.service_name || instance.service_id} · {traceEnv || 'environment unknown'} · {formatCount(instance.evidence_span_ids?.length || 0)} evidence spans
          </div>
        </div>
        {expanded
          ? <ChevronUp size={14} style={{ color: palette.text.muted, flexShrink: 0 }} />
          : <ChevronDown size={14} style={{ color: palette.text.muted, flexShrink: 0 }} />}
      </button>

      {expanded && (
        <div className="border-t border-border-subtle px-3 py-3">
          {loading ? (
            <div className="text-xs text-text-muted">Loading trace preview...</div>
          ) : error ? (
            <div className="text-xs" style={{ color: palette.status.danger }}>{error}</div>
          ) : preview ? (
            <div className="space-y-3">
              <div className="grid gap-2 sm:grid-cols-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-text-muted">Duration</div>
                  <div className="text-xs text-text-primary">{formatDuration(preview.duration_ms)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-text-muted">Spans</div>
                  <div className="text-xs text-text-primary">{formatCount(preview.span_count)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-text-muted">Tokens</div>
                  <div className="text-xs text-text-primary">{formatTokens(preview.tokens)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-text-muted">Cost</div>
                  <div className="text-xs text-text-primary">{formatCost(preview.cost)}</div>
                </div>
              </div>
              {preview.input != null && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-text-muted">Input</div>
                  <div className="mt-0.5 line-clamp-3 text-xs text-text-primary">{preview.input}</div>
                </div>
              )}
              {preview.output != null && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-text-muted">Output</div>
                  <div className="mt-0.5 line-clamp-3 text-xs text-text-primary">{preview.output}</div>
                </div>
              )}
              <a
                href={traceUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
                style={{ color: palette.accents.primary.base }}
              >
                See full trace <ExternalLink size={11} />
              </a>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
